// Wunli.com Print Studio · Compatibility shim — proxies legacy mock imports to real API
import {
  authApi, adminApi, usersApi, resellersApi, cardsApi, templatesApi,
  productsApi, ordersApi, paymentsApi, mlmApi, discountsApi, chatApi,
  supportApi, aiApi, stripeApi, phoneApi, addonsApi, partnersApi, uploadApi,
  STATIC_FONTS, DEFAULT_MLM_STRUCTURE
} from './services/api';

export const mockFonts = STATIC_FONTS;
export const mockDesignTemplates = [];
export const mockProducts = [];
export const mockPaymentMethods = [];
export const mockMLMData = DEFAULT_MLM_STRUCTURE;
export const mockResellers = [];
export const mockOrders = [];
export const mockSocialMediaProfiles = [];
export const mockBusinessCard = {
  id: 0, companyName: '', personalName: '', customerName: '', phone: '', email: '',
  website: '', address: '', logo: '', slogan: '', templateId: 1, fontId: 1,
  orientation: 'horizontal', qrCode: ''
};
export const mockAdminSettings = {
  businessInfo: { name: 'Wunli.com Print Studio', email: 'carlos@wunli.com', phone: '+1-214-949-7999' },
  subscription: { plan: 'Owner', expires: '2099-12-31', cardsGenerated: 0, limit: 999999 },
  mlmSettings: { resellerFee: 250.00, commissionStructure: DEFAULT_MLM_STRUCTURE.commissionStructure, payoutSchedule: 'monthly', minimumPayout: 50.00 }
};

export const mockApi = {
  saveBusinessCard: async (data) => {
    const r = await cardsApi.create(data);
    return r ? { success: true, id: r.data?.id } : { success: false };
  },
  generatePDF: async (cardData) => {
    if (!cardData?.id) return { success: false };
    const r = await cardsApi.generatePdf(cardData.id);
    return r ? { success: true, url: r.data?.url } : { success: false };
  },
  uploadLogo: async (file) => {
    const r = await uploadApi.logo(file);
    return r ? { success: true, url: r.data?.url } : { success: false };
  },
  processPayment: async (paymentData) => {
    const r = await paymentsApi.process(paymentData);
    return r ? { success: true, transactionId: r.data?.transactionId, status: 'completed' } : { success: false };
  },
  updatePaymentSettings: async (methodId, settings) => {
    const r = await paymentsApi.updateMethod(methodId, settings);
    return r ? { success: true } : { success: false };
  },
  addDesignTemplate: async (template) => {
    const r = await templatesApi.create(template);
    return r ? { success: true } : { success: false };
  },
  updateDesignTemplate: async (id, template) => {
    const r = await templatesApi.update(id, template);
    return r ? { success: true } : { success: false };
  },
  deleteDesignTemplate: async (id) => {
    const r = await templatesApi.remove(id);
    return r ? { success: true } : { success: false };
  },
  upgradeToReseller: async (paymentData) => {
    const r = await resellersApi.upgrade(paymentData);
    return r ? { success: true, transactionId: r.data?.transactionId, status: 'completed', accountType: 'reseller' } : { success: false };
  },
  getMLMData: async () => {
    const r = await mlmApi.get();
    return r ? { success: true, data: r } : { success: true, data: DEFAULT_MLM_STRUCTURE };
  },
  updateMLMSettings: async (settings) => {
    const r = await mlmApi.update(settings);
    return r ? { success: true } : { success: false };
  },
  getResellerData: async (resellerId) => {
    const all = await resellersApi.list();
    const list = all?.data || [];
    const reseller = list.find(r => r.id === resellerId);
    return reseller ? { success: true, data: reseller } : { success: false };
  },
  updateResellerStatus: async (resellerId, status) => {
    const r = await resellersApi.update(resellerId, { isActive: status === 'active' });
    return r ? { success: true } : { success: false };
  },
  getProducts: async () => {
    const r = await productsApi.list();
    return r ? { success: true, data: r } : { success: false, data: [] };
  },
  addProduct: async (productData) => {
    const r = await productsApi.create(productData);
    return r ? { success: true, product: productData } : { success: false };
  },
  updateProduct: async (productId, productData) => {
    const r = await productsApi.update(productId, productData);
    return r ? { success: true } : { success: false };
  },
  deleteProduct: async (productId) => {
    const r = await productsApi.remove(productId);
    return r ? { success: true } : { success: false };
  },
  connectSocialMedia: async (platform, credentials) => {
    console.log('[connectSocialMedia] not implemented backend-side', platform);
    return { success: true, connected: true, platform };
  },
  postToSocialMedia: async (platforms, content) => {
    console.log('[postToSocialMedia] not implemented backend-side', platforms);
    return { success: true, posted: platforms.length, timestamp: new Date().toISOString() };
  },
  // Direct fetchers
  fetchResellers: async () => (await resellersApi.list())?.data || [],
  fetchOrders: async () => (await adminApi.orders()) || [],
  fetchAnalytics: async () => await adminApi.analytics(),
  fetchUsers: async (page = 1, pageSize = 50, userType = null) => await adminApi.users(page, pageSize, userType),
  fetchTemplates: async () => (await templatesApi.list()) || [],
  fetchPaymentMethods: async () => (await paymentsApi.listMethods()) || [],
  fetchSystemSettings: async () => await adminApi.settings(),
  saveSystemSettings: async (data) => {
    const r = await adminApi.updateSettings(data);
    return r ? { success: true } : { success: false };
  },
  createUser: async (data) => {
    const r = await usersApi.create(data);
    return r ? { success: true, id: r.data?.id } : { success: false };
  },
  updateUser: async (id, data) => {
    const r = await usersApi.update(id, data);
    return r ? { success: true } : { success: false };
  },
  deleteUser: async (id) => {
    const r = await usersApi.remove(id);
    return r ? { success: true } : { success: false };
  },
  listChatSessions: async () => (await chatApi.listSessions()) || [],
  listChatMessages: async (sid) => (await chatApi.listMessages(sid)) || [],
  sendChatMessage: async (sid, message) => {
    const r = await chatApi.sendMessage(sid, message);
    return r ? { success: true } : { success: false };
  },
  listDiscountCodes: async () => (await discountsApi.list()) || [],
  createDiscountCode: async (data) => {
    const r = await discountsApi.create(data);
    return r ? { success: true } : { success: false };
  },
  deleteDiscountCode: async (id) => {
    const r = await discountsApi.remove(id);
    return r ? { success: true } : { success: false };
  },
  // Support Team
  listSupportTeam: async () => (await supportApi.list()) || [],
  createSupportMember: async (data) => {
    const r = await supportApi.create(data);
    return r ? { success: true, id: r.data?.id } : { success: false };
  },
  updateSupportMember: async (id, data) => {
    const r = await supportApi.update(id, data);
    return r ? { success: true } : { success: false };
  },
  deleteSupportMember: async (id) => {
    const r = await supportApi.remove(id);
    return r ? { success: true } : { success: false };
  },
  // AI Settings + Training
  fetchAISettings: async () => await aiApi.getSettings(),
  saveAISettings: async (data) => {
    const r = await aiApi.updateSettings(data);
    return r ? { success: true } : { success: false };
  },
  fetchAITraining: async () => (await aiApi.listTraining()) || [],
  addAITraining: async (data) => {
    const r = await aiApi.addTraining(data);
    return r ? { success: true } : { success: false };
  },
  deleteAITraining: async (id) => {
    const r = await aiApi.removeTraining(id);
    return r ? { success: true } : { success: false };
  },
  // Stripe + Phone
  stripeStatus: async () => await stripeApi.status(),
  stripeIntent: async (amount, orderId) => await stripeApi.createIntent(amount, orderId),
  sendPhoneCode: async (phone) => await phoneApi.sendCode(phone),
  verifyPhoneCode: async (phone, code) => await phoneApi.verifyCode(phone, code),
  // Add-ons
  fetchAddons: async () => (await addonsApi.list()) || [],
  createAddon: async (data) => {
    const r = await addonsApi.create(data);
    return r ? { success: true, id: r.data?.id } : { success: false };
  },
  updateAddon: async (id, data) => {
    const r = await addonsApi.update(id, data);
    return r ? { success: true } : { success: false };
  },
  deleteAddon: async (id) => {
    const r = await addonsApi.remove(id);
    return r ? { success: true } : { success: false };
  },
  // Partners
  fetchPartners: async () => (await partnersApi.list()) || [],
  createPartner: async (data) => {
    const r = await partnersApi.create(data);
    return r ? { success: true, id: r.data?.id } : { success: false };
  },
  updatePartner: async (id, data) => {
    const r = await partnersApi.update(id, data);
    return r ? { success: true } : { success: false };
  },
  deletePartner: async (id) => {
    const r = await partnersApi.remove(id);
    return r ? { success: true } : { success: false };
  }
};
